import React, { useMemo, useCallback, memo } from "react";
import { ChevronDown, ChevronRight, Star } from "lucide-react";
import styles from "./Styles/TreeNode.module.css";
import { useDispatch, useSelector } from "react-redux";

const getClassNames = (...classes) => classes.filter(Boolean).join(" ");

// Normalize string for comparison
const normalize = (str) => {
  if (!str || typeof str !== "string") return "";
  try {
    let fixed = str.trim();
    fixed = fixed
      .replace(/]\s*"(\w+)":/g, '], "$1":')
      .replace(/"\s*"(\w+)":/g, '", "$1":')
      .replace(/(\d+)\s*"(\w+)":/g, '$1, "$2":')
      .replace(/}\s*"(\w+)":/g, '}, "$1":')
      .replace(/"\]"/g, '"], "');
    return JSON.stringify(JSON.parse(fixed));
  } catch {
    console.warn("Failed to normalize:", str);
    return str.trim();
  }
};

// Fix malformed inventory hosts
const cleanInventoryHostsWrapper = (data) => {
  const fixString = (str) => {
    const fixed = str
      .replace(/"\s+"/g, '", "')
      .replace(/\]\s+\[/g, "], [")
      .replace(/(\d+)\s+"/g, '$1, "')
      .replace(/"\s+(\d+)/g, '", $1')
      .replace(/]\s+{/g, "], {")
      .replace(/}\s+{/g, "}, {")
      .replace(/"\s+\[/g, '", [')
      .replace(/}\s+"/g, '}, "')
      .replace(/\s+/g, " ")
      .trim();
    try {
      return JSON.stringify(JSON.parse(fixed));
    } catch {
      return str.trim();
    }
  };

  return data.map((item) => {
    const cleanedHosts = [];
    let buffer = "";

    item.inventory_hosts.forEach((entry) => {
      buffer += entry.trim();
      const bracketsOk =
        (buffer.match(/\[/g) || []).length ===
          (buffer.match(/\]/g) || []).length &&
        (buffer.match(/{/g) || []).length === (buffer.match(/}/g) || []).length;
      if (bracketsOk) {
        cleanedHosts.push(fixString(buffer));
        buffer = "";
      } else {
        buffer += " ";
      }
    });

    if (buffer.trim()) cleanedHosts.push(fixString(buffer));

    return {
      inventory_name: item.inventory_name,
      inventory_hosts: cleanedHosts,
    };
  });
};

const TreeNode = memo(
  ({
    tree,
    node,
    onToggle,
    onSelect,
    onFavoriteToggle,
    listOfFavorites,
    inventoryName = "",
  }) => {
    const cleanedFavorites = useMemo(
      () => cleanInventoryHostsWrapper(listOfFavorites),
      [listOfFavorites]
    );

    const isAutoFavorite = useMemo(() => {
      return cleanedFavorites.some(
        (fav) =>
          fav.inventory_name === inventoryName &&
          fav.inventory_hosts.some(
            (host) => normalize(String(host)) === normalize(String(node.label))
          )
      );
    }, [cleanedFavorites, node.label, inventoryName]);

    // Assign isFavorite to node if it's an auto favorite and not already set
    if (!node.hasOwnProperty("isFavorite") && isAutoFavorite) {
      node.isFavorite = true;
    }

    const isFavorite = node.isFavorite === true;

    const levelClass = styles[`level${node.level}`] || "";

    const handleToggle = useCallback(
      (e) => {
        e.stopPropagation();
        onToggle(node.id);
      },
      [onToggle, node.id]
    );

    const handleSelect = useCallback(() => {
      onSelect(node.id);
    }, [onSelect, node.id]);

    const handleFavorite = useCallback(
      (e) => {
        e.stopPropagation();
        onFavoriteToggle(node);
      },
      [onFavoriteToggle, node]
    );

    return (
      <div className={styles.selectNone}>
        <div
          className={getClassNames(
            styles.treeNode,
            node.selected && styles.selected,
            node.level === 0 && styles.rootNode,
            levelClass,
            node.expanded && styles.expandedNode
          )}
          onClick={handleSelect}
        >
          <div
            style={{ paddingLeft: `${node.level * 20}px` }}
            className={styles.treeNodeContent}
          >
            {node.children !== undefined ? (
              <div onClick={handleToggle} className={styles.toggleIcon}>
                {node.expanded ? (
                  <ChevronDown className={styles.icon} />
                ) : (
                  <ChevronRight className={styles.icon} />
                )}
              </div>
            ) : (
              <div className={styles.emptySpace}></div>
            )}

            <div className={styles.labelContainer}>
              <span
                className={getClassNames(
                  styles.label,
                  node.isLeaf && styles.leafNode,
                  node.selected && styles.selectedText
                )}
              >
                {node.label}
              </span>

              {node.column && (
                <span className={styles.columnInfo}>{node.column}</span>
              )}

              <span
                onClick={handleFavorite}
                title="Mark as Favorite"
                style={{ cursor: "pointer" }}
              >
                <Star
                  className={styles.icon}
                  fill={isFavorite ? "#facc15" : "#d1d5db"}
                  color={isFavorite ? "#facc15" : "#d1d5db"}
                />
              </span>
            </div>
          </div>
        </div>

        {node.expanded && node.children && (
          <div className={styles.childrenContainer}>
            {node.children.map((child) => (
              <TreeNode
                key={child.id}
                tree={tree}
                node={child}
                onToggle={onToggle}
                onSelect={onSelect}
                onFavoriteToggle={onFavoriteToggle}
                listOfFavorites={listOfFavorites}
                inventoryName={inventoryName}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
);

export default TreeNode;
